import { Rect } from '../../../platform/geom/Rect';

export interface Layout {
    area: Rect;
}

export interface GridLayout<T> {
    readonly area: Rect;
    readonly cols: number;
    readonly colWidth: number;
    readonly rows: number;
    readonly rowHeight: number;
    readonly cells: CellLayout<T>[];
}

export type CellLayout<T> = T & { area: Rect };

export const GridLayout = {

    empty: Object.freeze({ area: Rect.empty, cols: 0, rows: 0, cells: [] }),

    from<T>(area: Rect, cols: number, cellValues: T[]): GridLayout<T> {

        const rows = Math.ceil(cellValues.length / cols);

        const colWidth = cols === 0 ? 0 : area.width / cols;
        const rowHeight = rows === 0 ? 0 : area.height / rows;

        const cellArea = (index: number) => {
            const ga = area;
            const cw = ga.width / cols;
            const ch = ga.height / rows;
            return new Rect(
                ga.left + (cw * (index % cols)),
                ga.top + (ch * Math.floor(index / cols)),
                cw,
                ch,
            );
        };

        const cells = cellValues
            .map((x, i) => ({ ...x, area: cellArea(i) }));

        return Object.freeze({ area, cols, colWidth, rows, rowHeight, cells });
    },

    merge<T>(gl: GridLayout<T>, comparer: (a: T, b: T) => boolean): GridLayout<T> {

        const merged = gl.cells
            .reduce((rslt, curr) => {
                const prev = rslt[rslt.length - 1];
                if (prev && comparer(prev, curr)) {
                    prev.area = Rect.fromMany([prev.area, curr.area]);
                    return rslt;
                }
                else {
                    return [...rslt, curr];
                }
            }, []);

        return Object.freeze({ ...gl, cells: merged });
    },
};

export type LayoutGroup<T> = Layout & T;

export const LayoutGroup = {
    from<T extends Layout, G extends Record<string, T>>(layouts: G): LayoutGroup<G> {
        const area = Rect.fromMany(Object.values(layouts).map(x => x.area));
        return { area, ...layouts };
    }
};
